import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { APIRequest, QueryParamsUtil, UserStore } from '@reverbdotcom/discovery-ui';
import { trackEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import Loading from '../shared/loading';
import Cookies from 'js-cookie';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { IS_TEST } from '@reverbdotcom/env';
import { mparticleEmailFields } from '@reverbdotcom/commons/src/mparticle_email_fields_helper';
import uuid from '@reverbdotcom/commons/src/uuid';
import { trackFacebookEvent, LEAD_EVENT, MAILING_LIST_CONTENT_NAME } from '@reverbdotcom/commons/src/facebook';
import { RCCloseButton } from '@reverbdotcom/cadence/components';

const COOKIE_DISMISS_KEY = 'weekly_newsletter_dismiss';
const COOKIE_SUBMITTED_KEY = 'weekly_newsletter_submitted';
const EXPIRATION_DATE = new Date(moment().add(30, 'days'));

const ARTICLE_BODY_SELECTOR = '.blog-post__content';
const SCROLL_EVENT = 'scroll.email.signup';
const SCROLL_TOLERANCE = 25;

const AID_PARAM = '_aid';
const AID_VALUE = 'newsletter';

const COMPONENT_NAME = 'Pop Up Newsletter Signup';

function surpressFromNewsLetter() {
  return QueryParamsUtil.getParamValue(AID_PARAM) === AID_VALUE;
}

function SuccessMessage({ onClick = () => {} }) {
  return (
    <div className="align-center">
      <button
        type="submit"
        onClick={onClick}
        className="button button--primary"
      >
        {I18n.t('js.emailSignup.close')}
      </button>
    </div>
  );
}

SuccessMessage.propTypes = { onClick: PropTypes.func };

export function EmailSignup({
  userSignedup = Cookies.get(COOKIE_SUBMITTED_KEY),
  userDismissed = Cookies.get(COOKIE_DISMISS_KEY),
  linkedFromNewsLetter = surpressFromNewsLetter(),
  userEmail = null,
  signupUrl = null,
  articleSlug = '',
  signupUuid = uuid(),
}) {
  const user = useUser();

  // Use user email from context if userEmail prop is not provided
  const defaultUserEmail = userEmail || user?.email || null;

  const [submitted, setSubmitted] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [active, setActive] = useState(false);
  const [tracked, setTracked] = useState(false);
  const [email, setEmail] = useState(defaultUserEmail);
  const [title, setTitle] = useState(I18n.t('js.emailSignup.default.title'));
  const [message, setMessage] = useState(I18n.t('js.emailSignup.default.message'));

  const containerRef = useRef(null);

  const signUpReason = useMemo(() =>
    `article_footer_${articleSlug.replaceAll('-', '_')}`,
  [articleSlug],
  );

  const showGdpr = useMemo(() => UserStore.getState().gdpr, []);

  const trackView = useCallback(() => {
    if (!tracked) {
      trackEvent({
        eventName: MParticleEventName.ComponentView,
        componentName: COMPONENT_NAME,
        source: signUpReason,
      });
      setTracked(true);
    }
  }, [tracked, signUpReason]);

  const removeWindowEvent = useCallback(() => {
    $(window).off(SCROLL_EVENT);
  }, []);

  const addWindowEvent = useCallback(() => {
    // this callback was causing flaking karma tests
    // presumably due to some incomplete test setup
    // as a quick fix, we just won't execute this callback in test
    if (IS_TEST) { return; }

    $(window).on(SCROLL_EVENT, () => {
      const articleHeight = $(ARTICLE_BODY_SELECTOR).innerHeight()
        - $(ARTICLE_BODY_SELECTOR).position().top;
      const scrollPosition = $(window).scrollTop();
      const percent = (scrollPosition / articleHeight) * 100;

      if (percent > SCROLL_TOLERANCE) {
        setActive(true);
        trackView();
      } else {
        setActive(false);
      }
    });
  }, [trackView]);

  const updateFacebook = useCallback(() => {
    trackFacebookEvent(
      LEAD_EVENT,
      { content_name: MAILING_LIST_CONTENT_NAME },
      { eventID: signupUuid },
    );
  }, []);

  const success = useCallback(() => {
    updateFacebook();

    setSubmitted(true);
    setDisableSubmit(false);
    setTitle(I18n.t('js.emailSignup.submitted.title'));
    setMessage(I18n.t('js.emailSignup.submitted.message'));
  }, [updateFacebook]);

  const fail = useCallback((e) => {
    let newTitle;
    let newMessage;

    if (e.status === 400) {
      newTitle = I18n.t('js.emailSignup.invalid.title');
      newMessage = I18n.t('js.emailSignup.invalid.message');
    } else {
      newTitle = I18n.t('js.emailSignup.error.title');
      newMessage = I18n.t('js.emailSignup.error.message');
    }

    setSubmitted(false);
    setDisableSubmit(false);
    setTitle(newTitle);
    setMessage(newMessage);
  }, []);

  const onDismiss = useCallback(() => {
    if (!submitted) {
      Cookies.set(COOKIE_DISMISS_KEY, true, { expires: EXPIRATION_DATE });
    }

    setDismissed(true);
    removeWindowEvent();
  }, [submitted, removeWindowEvent]);

  const onSubmit = useCallback((event) => {
    event.preventDefault();

    if (email) {
      setDisableSubmit(true);

      if (email.length > 255) {
        const emailTooLongTitle = I18n.t('js.emailSignup.emailTooLong.title');
        const emailTooLongMessage = I18n.t('js.emailSignup.emailTooLong.message');

        setSubmitted(false);
        setDisableSubmit(false);
        setTitle(emailTooLongTitle);
        setMessage(emailTooLongMessage);

        return;
      }

      Cookies.set(COOKIE_SUBMITTED_KEY, true);

      const params = {
        email,
        signup_reason: signUpReason,
        signup_uuid: signupUuid,
        ...mparticleEmailFields(),
      };

      if (showGdpr) {
        params.consented_to_gdpr = true;
      }
      const req = APIRequest.post(signupUrl, params);

      req.success(success);
      req.fail(fail);
    }
  }, [email, signUpReason, showGdpr, signupUrl, success, fail]);

  const onSetEmail = useCallback((event) => {
    setEmail(event.target.value);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      addWindowEvent();
    }

    return () => {
      removeWindowEvent();
    };
  }, [addWindowEvent, removeWindowEvent]);

  const displayStyle = useMemo(() => ({
    display: dismissed ? 'none' : 'block',
  }), [dismissed]);

  const renderGdprTerms = () => {
    if (!showGdpr) { return null; }

    return (
      <div className="email-signup__terms">
        {I18n.t('js.emailSignup.gdprTerms', { buttonText: I18n.t('js.emailSignup.subscribeButton') })}
      </div>
    );
  };

  const renderForm = () => {
    if (submitted) {
      return <SuccessMessage onClick={onDismiss} />;
    }

    return (
      <form onSubmit={onSubmit}>
        <div className="input-group">
          <input
            type="email"
            name="email"
            className="input-group__input"
            value={email}
            onChange={onSetEmail}
            placeholder={I18n.t('js.emailSignup.inputEmailPlaceholder')}
            disabled={disableSubmit}
            aria-labelledby="email-signup-title"
            aria-describedby="email-signup-description"
          />
          <button
            type="submit"
            disabled={disableSubmit}
            className="input-group__button"
          >
            <Loading
              loaded={!disableSubmit}
              size="small"
              color="white"
            >
              {I18n.t('js.emailSignup.subscribeButton')}
            </Loading>
          </button>
        </div>
      </form>
    );
  };

  const componentClassNames = useMemo(() =>
    classNames({ 'email-signup': true, active }),
  [active],
  );

  if (userSignedup) { return null; }
  if (userDismissed) { return null; }
  if (linkedFromNewsLetter) { return null; }

  return (
    <section ref={containerRef} className={componentClassNames} style={displayStyle}>
      <div className="email-signup__close-button">
        <RCCloseButton onClick={onDismiss} inverted variant="filled" />
      </div>
      <div className="email-signup__message-wrapper">
        <h2 className="email-signup__message-title" id="email-signup-title">{title}</h2>
        <p className="email-signup__message-subtitle" id="email-signup-description">{message}</p>
      </div>
      <div className="email-signup__form-wrapper">
        {renderForm()}
        {renderGdprTerms()}
      </div>
    </section>
  );
}

EmailSignup.propTypes = {
  userSignedup: PropTypes.bool,
  userDismissed: PropTypes.bool,
  linkedFromNewsLetter: PropTypes.bool,
  signupUrl: PropTypes.string,
  articleSlug: PropTypes.string,
};

export default EmailSignup;
